import type { ComponentType, SVGAttributes } from "react";
import {
  AspectRatioIcon,
  Button,
  Column,
  CopyIcon,
  GalleryIcon,
  PlayIcon,
  RefreshIcon,
  Row,
  ShuffleIcon,
  Skeleton,
  SkeletonBox,
  StylesIcon,
} from "@hightouch/htui";
import type {
  SuggestedAction,
  SuggestedActionId,
} from "src/pages/ad-studio/insights/ad-suggested-actions";

// Riff gets the copy glyph rather than a sparkle: a sparkle reads as "AI did
// this" on a row where every button is an AI move.
const ICONS: Record<
  SuggestedActionId,
  ComponentType<SVGAttributes<SVGElement>>
> = {
  riff: CopyIcon,
  refresh: RefreshIcon,
  convert_to_motion: PlayIcon,
  remix_in_style: StylesIcon,
  resize: AspectRatioIcon,
  cut_down: ShuffleIcon,
  create_statics: GalleryIcon,
};

type Props = {
  actions: SuggestedAction[];
  onSelect: (action: SuggestedAction) => void;
};

export function AdSuggestedActionsRow({ actions, onSelect }: Props) {
  if (actions.length === 0) return null;

  return (
    <Column gap={3} aria-label="Suggested actions" role="group">
      <Row align="center" gap={2} flexWrap="wrap">
        {actions.map((action, index) => (
          <Button
            key={action.id}
            variant={index === 0 ? "primary" : "secondary"}
            size="md"
            icon={ICONS[action.id]}
            onClick={() => onSelect(action)}
          >
            {action.label}
          </Button>
        ))}
      </Row>
    </Column>
  );
}

/** Roughly the width the three labels settle at, so the row keeps its shape. */
const PLACEHOLDER_WIDTHS = ["152px", "164px", "148px"];

/** Stands in for the row while the styles decide the third move. */
export function AdSuggestedActionsRowSkeleton() {
  return (
    <Skeleton isLoading>
      <Column gap={3}>
        <Row align="center" gap={2}>
          {PLACEHOLDER_WIDTHS.map((width) => (
            <SkeletonBox
              key={width}
              height={8}
              width={width}
              borderRadius="lg"
            />
          ))}
        </Row>
      </Column>
    </Skeleton>
  );
}
